import { addToCart } from '@nova/utils'

const CUSTOM_TEXT_REQUIRED_MESSAGE = 'Please enter your custom text before adding to cart.'

function variantNeedsCustomText(variant, optionNames) {
  if (!variant?.options?.length) return false

  const customTextOptionIndex = optionNames.findIndex((name) =>
    /custom text\?/i.test(name),
  )

  if (customTextOptionIndex !== -1) {
    return variant.options[customTextOptionIndex] === 'Yes'
  }

  return variant.options.some((option) => /custom text/i.test(option))
}

function getCustomTextConfig(field) {
  const configEl = field.parentElement?.querySelector('[data-custom-text-config]')
  if (!configEl) return null

  try {
    return JSON.parse(configEl.textContent)
  } catch {
    return null
  }
}

function getVariantFromConfig(config, variantId) {
  if (!config?.variants?.length || !variantId) return null
  return config.variants.find((variant) => String(variant.id) === String(variantId))
}

function parseLineItemProperties(form) {
  const properties = {}
  const formData = new FormData(form)
  const propertyPattern = /^properties\[(.+)\]$/

  for (const key of formData.keys()) {
    const match = propertyPattern.exec(key)
    if (!match) continue

    const value = formData.get(key)?.toString().trim()
    if (value) {
      properties[match[1]] = value
    }
  }

  return properties
}

function setCustomTextFieldState(field, isRequired) {
  const textarea = field.querySelector('.js-custom-text-input')
  const error = field.querySelector('.js-custom-text-error')
  if (!textarea) return

  field.hidden = !isRequired
  field.setAttribute('aria-hidden', isRequired ? 'false' : 'true')
  field.classList.toggle('line-item-property__field--visible', isRequired)

  if (isRequired) {
    textarea.setAttribute('required', '')
  } else {
    textarea.removeAttribute('required')
    textarea.value = ''
    field.classList.remove('line-item-property__field--error')
    if (error) {
      error.hidden = true
      error.textContent = ''
    }
  }
}

function syncCustomTextField(field) {
  const config = getCustomTextConfig(field)
  const formId = field.dataset.formId
  const form = formId ? document.getElementById(formId) : null
  const variantId = form?.querySelector('[name="id"]')?.value
  const variant = getVariantFromConfig(config, variantId)
  const isRequired = variantNeedsCustomText(variant, config?.options || [])

  setCustomTextFieldState(field, isRequired)
  field.dataset.customTextRequired = isRequired ? 'true' : 'false'
}

function watchVariantInput(form, field) {
  const variantInput = form.querySelector('[name="id"]')
  if (!variantInput) return

  const sync = () => syncCustomTextField(field)
  variantInput.removeEventListener('change', field._customTextSync)
  variantInput.addEventListener('change', sync)
  field._customTextSync = sync
  sync()
}

function bindCustomTextField(field) {
  if (field.dataset.customTextBound === 'true') return
  field.dataset.customTextBound = 'true'

  const formId = field.dataset.formId
  const form = formId ? document.getElementById(formId) : null
  if (!form) return

  watchVariantInput(form, field)
}

function initCustomTextFields(container = document) {
  const fields = container.querySelectorAll('[data-custom-text-field]')
  for (const field of fields) {
    bindCustomTextField(field)
  }
}

function showCustomTextError(field) {
  const error = field.querySelector('.js-custom-text-error')
  field.classList.add('line-item-property__field--error')
  if (error) {
    error.textContent = CUSTOM_TEXT_REQUIRED_MESSAGE
    error.hidden = false
  }
}

function clearCustomTextError(field) {
  const error = field.querySelector('.js-custom-text-error')
  field.classList.remove('line-item-property__field--error')
  if (error) {
    error.hidden = true
    error.textContent = ''
  }
}

function validateCustomTextFields(form) {
  const fields = document.querySelectorAll(
    `[data-custom-text-field][data-form-id="${form.id}"]`,
  )

  for (const field of fields) {
    if (field.dataset.customTextRequired !== 'true') continue

    const textarea = field.querySelector('.js-custom-text-input')
    if (!textarea?.value.trim()) {
      showCustomTextError(field)
      textarea?.focus()
      return false
    }

    clearCustomTextError(field)
  }

  return true
}

function measureProductDescriptionExpandable(root) {
  const inner = root.querySelector('.js-product-description-inner')
  const content = root.querySelector('.product__description-content')
  const toggle = root.querySelector('.js-product-description-toggle')
  const label = root.querySelector('.js-product-description-toggle-label')

  if (!inner || !toggle || !label) return

  const collapsedHeight =
    Number.parseInt(
      getComputedStyle(root).getPropertyValue('--product-description-collapsed-height'),
      10,
    ) || 200

  inner.style.maxHeight = 'none'
  const fullHeight = Math.max(content?.scrollHeight ?? 0, inner.scrollHeight)
  const needsTruncate = fullHeight > collapsedHeight + 1

  root.dataset.descriptionFullHeight = String(fullHeight)
  root.dataset.descriptionCollapsedHeight = String(collapsedHeight)

  if (needsTruncate) {
    root.classList.remove('product__description-expandable--full')
    root.classList.add('product__description-expandable--truncated')
    toggle.hidden = false

    const expanded = root.classList.contains('is-expanded')
    inner.style.maxHeight = expanded ? `${fullHeight}px` : `${collapsedHeight}px`
    return
  }

  root.classList.add('product__description-expandable--full')
  root.classList.remove('product__description-expandable--truncated', 'is-expanded')
  toggle.hidden = true
  toggle.setAttribute('aria-expanded', 'false')
  label.textContent = root.dataset.showMore
  inner.style.maxHeight = 'none'
}

function bindProductDescriptionExpandable(root) {
  if (root.dataset.descriptionToggleBound === 'true') return
  root.dataset.descriptionToggleBound = 'true'

  const inner = root.querySelector('.js-product-description-inner')
  const toggle = root.querySelector('.js-product-description-toggle')
  const label = root.querySelector('.js-product-description-toggle-label')
  const content = root.querySelector('.product__description-content')

  if (!inner || !toggle || !label) return

  toggle.addEventListener('click', () => {
    if (root.classList.contains('product__description-expandable--full')) return

    const fullHeight = Number.parseInt(root.dataset.descriptionFullHeight, 10)
    const collapsedHeight = Number.parseInt(root.dataset.descriptionCollapsedHeight, 10)
    const expanded = root.classList.toggle('is-expanded')

    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false')
    label.textContent = expanded ? root.dataset.showLess : root.dataset.showMore
    inner.style.maxHeight = expanded ? `${fullHeight}px` : `${collapsedHeight}px`
  })

  if (root.dataset.descriptionResizeBound !== 'true') {
    root.dataset.descriptionResizeBound = 'true'

    const remeasure = () => measureProductDescriptionExpandable(root)

    if (content && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(remeasure)
      observer.observe(content)
    }

    content?.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', remeasure, { once: true })
      }
    })

    window.addEventListener('load', remeasure, { once: true })
    window.addEventListener('resize', remeasure)
  }
}

function initProductDescriptionExpandables(container = document) {
  const expandables = container.querySelectorAll('.js-product-description-expandable')

  for (const root of expandables) {
    bindProductDescriptionExpandable(root)
    measureProductDescriptionExpandable(root)
  }
}

document.addEventListener('shopify:section:load', (event) => {
  initProductDescriptionExpandables(event.target)
  initCustomTextFields(event.target)
})

document.addEventListener('DOMContentLoaded', () => {
  initProductDescriptionExpandables()
  initCustomTextFields()
  const $forms = Array.from(document.querySelectorAll('.js-product-form') || [])
  const $sizeGuideBtns =
    document.querySelectorAll('.js-size-guide-button') || []
  const $sizeGuideDrawer = document.querySelector('.js-size-guide-drawer') || []

  if ($forms.length) {
    for (const $form of $forms) {
      const $submitButton = $form.querySelector('[type="submit"]')
      const $quantityInput = document.querySelector(
        `nova-quantity-input[data-form-id=${$form.getAttribute('id')}]`,
      )
      $form.addEventListener('submit', (event) => {
        event.preventDefault()

        if (!validateCustomTextFields($form)) return

        $submitButton.setAttribute('disabled', true)
        $submitButton.classList.add('loading')

        const formData = new FormData($form)
        const id = formData.get('id')
        const quantity = $quantityInput?.value || formData.get('quantity') || 1
        const properties = parseLineItemProperties($form)
        const cartItem = { id, quantity }

        if (Object.keys(properties).length > 0) {
          cartItem.properties = properties
        }

        addToCart(cartItem)
          .then(() => {
            for (const field of document.querySelectorAll(
              `[data-custom-text-field][data-form-id="${$form.id}"] .js-custom-text-input`,
            )) {
              field.value = ''
            }
          })
          .finally(() => {
            $submitButton.removeAttribute('disabled')
            $submitButton.classList.remove('loading')
          })
      })
    }

    document.addEventListener('nova:product:render-form', (event) => {
      const content = event.detail.html
      for (const $form of $forms) {
        if (!content) return
        $form.innerHTML = content.innerHTML
        for (const field of document.querySelectorAll(
          `[data-custom-text-field][data-form-id="${$form.id}"]`,
        )) {
          watchVariantInput($form, field)
        }
      }
    })
  }

  const accordionProductDetails = document.querySelector('.js-accordion-product-details')

  if (accordionProductDetails) {
    document.addEventListener('nova:product:render-accordion-product-details', (event) => {
      const content = event.detail.html
      accordionProductDetails.innerHTML = content.innerHTML
    })
  }

  if ($sizeGuideDrawer) {
    for (const $button of $sizeGuideBtns) {
      $button.addEventListener('click', () => {
        if ($sizeGuideDrawer.hasAttribute('open')) {
          $sizeGuideDrawer.hide()
        } else {
          $sizeGuideDrawer.show()
        }
      })
    }
  }
})
